import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getWorkOrderLabels, isWorkOrderStarred } from '../../data/workOrderStarLabels';
import '@ant-design/compatible/assets/index.css';
import { Button, List, Modal, Row } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	workOrdersRestCrudThunksForBuyer,
	appendWorkOrderNotesForBuyer,
	getBuyerMeetingToken,
	getBuyerOWLiveShortenedLink,
	buyerMarkNotificationSeen,
} from '../../thunks/work_orders_thunks';
import {
	fetchAllUsersForBuyers,
	addBuyerSubscribersForBuyers,
	addSupplierSubscribersForBuyers,
	removeBuyerSubscriberForBuyers,
	removeSupplierSubscriberForBuyers,
} from '../../thunks/work_orders_subscribers_thunks';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../../constants/NoteTypes';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { fetchAllWorkOrderNotesForBuyer } from '../../thunks/messages_thunks';
import AvatarGroup from '../avatar_group/AvatarGroup';
import SelectUsers from '../select_users/SelectUsers';
import RemoveSubscriberButton from './RemoveSubscriberButton';
import { ROLE_TYPES } from '../../utils/DataConstants';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../actions/work_orders_actions';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import {
	getContractorOrWorkOrderId,
	UNREAD_NOTES_NOTIFICATION_KEY,
} from '../../utils/WorkOrderUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { formatAndSortUserContacts } from '../../utils/DataFormatterUtils';
import CommonNotesChatPage from '../common_notes_chat_page/CommonNotesChatPage';
import CommonNoteInputForm from '../common_note_input_form/CommonNoteInputForm';

require('./WorkOrdersDetailNotesPage.less');

const WS_CONFIG = {
	entityIdName: 'workOrderId',
	entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
	newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
};

const WorkOrderNotesDetailPage = ({
	workOrder,
	currentUser,
	match,
	messages,
	searched_subscribers,
	fetchMessages,
	appendWorkOrderNotes,
	getMeetingToken,
	owShortenedLink,
	fetchUsers,
	addBuyerSubscribers,
	addSupplierSubscribers,
	removeBuyerSubscriber,
	removeSupplierSubscriber,
	markNotesSeen,
	updateWOUnreadNotification,
	updateWorkOrderPartiallyInRedux,
}) => {
	const workOrderId = match.params.id;

	const [subscriberModalVisible, setSubscriberModalVisible] = useState(false);
	const [addSubscriberModalVisible, setAddSubscriberModalVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [selectValue, setSelectValue] = useState<any[]>([]);
	const [addingSubscribers, setAddingSubscribers] = useState(false);

	// Mark notes as seen on mount
	useEffect(() => {
		if (nullSafeGetOrElse('unreadNotesNotificationIds.length', workOrder, 0) > 0) {
			markNotesSeen(nullSafeGet('unreadNotesNotificationIds', workOrder)).then(() => {
				updateWOUnreadNotification(workOrder);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchNotes = useCallback(() => {
		updateWorkOrderPartiallyInRedux({
			id: parseInt(workOrderId),
			[UNREAD_NOTES_NOTIFICATION_KEY]: false,
		});
		return fetchMessages(workOrderId);
	}, [fetchMessages, workOrderId, updateWorkOrderPartiallyInRedux]);

	// The "Added/Removed Label" entries live in the notes feed (persisted server-side),
	// so they only surface on a refetch. A label change updates this work order's labels
	// in Redux — from this user's own edit (optimistic store patch) or another user's
	// edit (label WS event applied by the detail page) — so refetch the notes whenever
	// the label set changes. This is the only reliable trigger for the editor's own page:
	// the WS server does not echo a publish back to its sender, so the new_note refetch
	// path never fires for the user who made the change.
	const labelIdsKey = getWorkOrderLabels(workOrder)
		.map((label) => label.id)
		.sort()
		.join(',');
	const prevLabelIdsKeyRef = useRef(labelIdsKey);
	useEffect(() => {
		if (prevLabelIdsKeyRef.current === labelIdsKey) return;
		prevLabelIdsKeyRef.current = labelIdsKey;
		fetchNotes();
	}, [labelIdsKey, fetchNotes]);

	const starred = isWorkOrderStarred(workOrder);
	const prevStarredRef = useRef(starred);
	useEffect(() => {
		if (prevStarredRef.current === starred) return;
		prevStarredRef.current = starred;
		fetchNotes();
	}, [starred, fetchNotes]);

	const appendNote = useCallback(
		(note: any) => appendWorkOrderNotes({ id: workOrder.id, note }),
		[appendWorkOrderNotes, workOrder]
	);

	const getMeetingTokenForRoom = useCallback(
		() => getMeetingToken(`${nullSafeGet('facility.buyerCompanyId', currentUser)}-${workOrder.id}`),
		[getMeetingToken, currentUser, workOrder]
	);

	const getOWShortenedLink = useCallback((url: string) => owShortenedLink(url), [owShortenedLink]);

	let subscribers = (workOrder.buyerSubscribers || []).concat(workOrder.supplierSubscribers || []);
	if (
		workOrder.supplierSubscribers &&
		workOrder.supplierSubscribers.length === 0 &&
		nullSafeGet('supplierFacility.id', workOrder)
	) {
		subscribers.push({
			nameGiven: nullSafeGet('supplierFacility.displayName', workOrder),
			nameFamily: '',
			email: nullSafeGet('supplierSubscriberEmails.0', workOrder),
		});
	}

	const filteredUsers = formatAndSortUserContacts(searched_subscribers.records);

	const handleSubscriberRemoved = useCallback(
		(item: any) => () => {
			// Returned so RemoveSubscriberButton can show a spinner until the call settles.
			if (item.contactType === ROLE_TYPES.BUYER) {
				return removeBuyerSubscriber(
					{ id: parseInt(workOrderId), removeBuyerSubscriberEmails: [item.email] },
					workOrder
				);
			}
			return removeSupplierSubscriber(
				{ id: parseInt(workOrderId), removeSupplierSubscriberEmails: [item.email] },
				workOrder
			);
		},
		[removeBuyerSubscriber, removeSupplierSubscriber, workOrderId, workOrder]
	);

	const handleAddSubscribers = useCallback(() => {
		setAddingSubscribers(true);
		const promises = [];
		const buyerEmails = selectedUsers.filter((u) => u.contactType === 'buyer').map((u) => u.email);
		const supplierEmails = selectedUsers
			.filter((u) => u.contactType === 'supplier')
			.map((u) => u.email);
		if (buyerEmails.length > 0) {
			promises.push(
				addBuyerSubscribers(
					{ id: parseInt(workOrderId), addBuyerSubscriberEmails: buyerEmails },
					workOrder
				)
			);
		}
		if (supplierEmails.length > 0) {
			promises.push(
				addSupplierSubscribers(
					{ id: parseInt(workOrderId), addSupplierSubscriberEmails: supplierEmails },
					workOrder
				)
			);
		}
		Promise.all(promises).then(() => {
			setSelectValue([]);
			setAddingSubscribers(false);
			setAddSubscriberModalVisible(false);
		});
	}, [selectedUsers, workOrderId, workOrder, addBuyerSubscribers, addSupplierSubscribers]);

	const handleOnChange = useCallback((val: any) => {
		const updatedVal = Array.isArray(val) ? val.map((v) => JSON.parse(v)) : JSON.parse(val);
		setSelectValue(updatedVal.map((v) => JSON.stringify(v)));
		setSelectedUsers(updatedVal);
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetchUsers = useCallback(
		debounce(
			(searchText: string) => {
				fetchUsers(searchText && searchText.trim(), workOrder['id']);
			},
			400,
			false
		),
		[fetchUsers, workOrder]
	);

	const handleSearchDebounced = useCallback(
		(value = '') => {
			const searchText = value && value.trim();
			setSearch(searchText);
			debouncedFetchUsers(searchText);
		},
		[debouncedFetchUsers]
	);

	const renderSubscribers = useCallback(
		() => (
			<>
				<div style={{ textAlign: 'right', padding: '0', height: '0px' }}>
					<Modal
						width={600}
						title={
							<div>
								<span style={{}}>Work Order {workOrderId}</span>
								{/* @ts-ignore */}
								<OWButtonWithSupplierManagedLock
									style={{ float: 'right', marginRight: '32px', marginTop: '-4px' }}
									onClick={() => {
										setSubscriberModalVisible(false);
										setAddSubscriberModalVisible(true);
										setSearch('');
										setSelectedUsers([]);
									}}
									type={'primary'}
								>
									Add People
								</OWButtonWithSupplierManagedLock>
							</div>
						}
						footer={null}
						closable={true}
						onCancel={() => setSubscriberModalVisible(false)}
						visible={subscriberModalVisible}
					>
						<List
							itemLayout="horizontal"
							dataSource={subscribers}
							renderItem={(item: any) => (
								<List.Item>
									<List.Item.Meta
										avatar={
											<ContactAvatar
												hidePopover={true}
												placement="bottomRight"
												userType="buyer"
												contact={item}
											/>
										}
										// @ts-ignore
										title={`${item.nameGiven} ${item.nameFamily}`}
										// @ts-ignore
										description={item.email}
									/>
									<RemoveSubscriberButton
										subscriber={item}
										onRemove={handleSubscriberRemoved}
										workOrder={workOrder}
									/>
								</List.Item>
							)}
						/>
					</Modal>
					<Modal
						width={600}
						title={
							<div>
								<span>Add Someone to Work Order {workOrderId}</span>
							</div>
						}
						footer={
							<div style={{ display: 'block', width: '100%' }}>
								<Button loading={addingSubscribers} onClick={handleAddSubscribers} type={'primary'}>
									Add Subscribers
								</Button>
							</div>
						}
						closable={true}
						onCancel={() => setAddSubscriberModalVisible(false)}
						visible={addSubscriberModalVisible}
					>
						<Row key={0}>
							{/* @ts-ignore */}
							<SelectUsers
								mode="multiple"
								style={{ width: '100%' }}
								placeholder="Begin to type a name or email to start searching"
								onSearch={handleSearchDebounced}
								onFocus={() => handleSearchDebounced()}
								onChange={handleOnChange}
								search={search}
								selectValue={selectValue}
								filteredUsers={filteredUsers}
								disabledUsers={subscribers}
								disableInactive={true}
							/>
						</Row>
					</Modal>
				</div>
				{/* @ts-ignore */}
				<AvatarGroup
					avatars={subscribers}
					onClick={() => setSubscriberModalVisible(true)}
					onAddClick={() => {
						setAddSubscriberModalVisible(true);
						setSearch('');
						setSelectedUsers([]);
					}}
				/>
			</>
		),
		[
			workOrderId,
			subscribers,
			subscriberModalVisible,
			addSubscriberModalVisible,
			addingSubscribers,
			search,
			selectValue,
			filteredUsers,
			handleSubscriberRemoved,
			handleAddSubscribers,
			handleSearchDebounced,
			handleOnChange,
			workOrder,
		]
	);

	const renderInputForm = useCallback(
		({ handleTyping, onSendNote, typingDetails, newNoteCount, onRefresh }) => (
			<CommonNoteInputForm
				appendNote={appendNote}
				currentUser={currentUser}
				workOrder={workOrder}
				onTyping={handleTyping}
				onSendNote={onSendNote}
				typingDetails={typingDetails}
				newNoteCount={newNoteCount}
				onRefresh={onRefresh}
				getMeetingToken={getMeetingTokenForRoom}
				getOWShortenedLink={getOWShortenedLink}
			/>
		),
		[appendNote, currentUser, workOrder, getMeetingTokenForRoom, getOWShortenedLink]
	);

	return (
		<CommonNotesChatPage
			entityId={String(getContractorOrWorkOrderId(workOrder))}
			fetchNotes={fetchNotes}
			userType={ROLE_TYPES.BUYER}
			heightSelector="buyer-workorder-detail-header"
			wsConfig={WS_CONFIG}
			currentUser={currentUser}
			messages={messages}
			renderSubscribers={renderSubscribers}
			renderInputForm={renderInputForm}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	messages: state.messages,
	currentUser: state.session.currentUser,
	searched_subscribers: state.searched_subscribers,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForBuyer.readOne(id)),
	fetchMessages: (workOrderId) => dispatch(fetchAllWorkOrderNotesForBuyer(workOrderId)),
	fetchUsers: (search, woId, filters?) => dispatch(fetchAllUsersForBuyers(search, woId, filters)()),
	addBuyerSubscribers: (obj, workOrder) => dispatch(addBuyerSubscribersForBuyers(obj)(workOrder)),
	addSupplierSubscribers: (obj, workOrder) =>
		dispatch(addSupplierSubscribersForBuyers(obj)(workOrder)),
	removeBuyerSubscriber: (obj, workOrder) =>
		dispatch(removeBuyerSubscriberForBuyers(obj)(workOrder)),
	removeSupplierSubscriber: (obj, workOrder) =>
		dispatch(removeSupplierSubscriberForBuyers(obj)(workOrder)),
	updateWorkOrderPartiallyInRedux: (workOrder) =>
		dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess(workOrder)),
	appendWorkOrderNotes: (workOrder) => dispatch(appendWorkOrderNotesForBuyer(workOrder)),
	getMeetingToken: (roomName) => dispatch(getBuyerMeetingToken(roomName)),
	owShortenedLink: (longUrl) => dispatch(getBuyerOWLiveShortenedLink(longUrl)),
	markNotesSeen: (ids) => dispatch(buyerMarkNotificationSeen(ids)),
	updateWOUnreadNotification: (workOrder) =>
		dispatch(
			WORK_ORDERS_CRUD_ACTION_CREATORS.updateSuccess({
				...workOrder,
				unreadNotesNotificationIds: [],
				isLastNoteUnreadByCurrentUser: false,
			})
		),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderNotesDetailPage as any)
);
